import { useEffect, useState } from "react";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { Info, Calendar } from "lucide-react";
import { istActiveCommonDateStr, readCache, writeCache } from "@/lib/ist";

const CN_CACHE_KEY = "jt_common_v1";

function RoundTable({ label, direct, house, ending }) {
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-[color:var(--brand-orange)]/40 bg-white shadow-[0_10px_28px_-18px_rgba(91,42,134,0.30)]">
      <div className="bg-[color:var(--brand-orange)] text-white text-center py-2 text-sm font-extrabold tracking-widest">
        {label}
      </div>
      <div className="grid grid-cols-3 bg-[color:var(--brand-purple-deep)] text-white text-xs sm:text-sm font-bold uppercase tracking-wider">
        <div className="py-2 text-center border-r border-white/20">Direct</div>
        <div className="py-2 text-center border-r border-white/20">House</div>
        <div className="py-2 text-center">Ending</div>
      </div>
      <div className="grid grid-cols-3 text-center">
        <div className="py-4 font-display font-extrabold text-2xl sm:text-3xl text-[color:var(--brand-purple-deep)] border-r border-[hsl(var(--border))]" data-testid={`${label.toLowerCase()}-direct`}>
          {direct.map((n) => String(n).padStart(2, "0")).join(", ")}
        </div>
        <div className="py-4 font-display font-extrabold text-2xl sm:text-3xl text-[color:var(--brand-purple-deep)] border-r border-[hsl(var(--border))]" data-testid={`${label.toLowerCase()}-house`}>
          {house}
        </div>
        <div className="py-4 font-display font-extrabold text-2xl sm:text-3xl text-[color:var(--brand-purple-deep)]" data-testid={`${label.toLowerCase()}-ending`}>
          {ending}
        </div>
      </div>
    </div>
  );
}

export default function CommonNumber() {
  // Instant paint from cache when active IST date matches
  const [data, setData] = useState(() => {
    const cached = readCache(CN_CACHE_KEY);
    if (cached && cached.date === istActiveCommonDateStr()) return cached;
    return null;
  });
  const [loading, setLoading] = useState(() => {
    const cached = readCache(CN_CACHE_KEY);
    return !(cached && cached.date === istActiveCommonDateStr());
  });

  useSEO({
    title: "Juwai Teer Common Number | Daily Common Numbers Chart",
    description: "Juwai Teer Common Number reference for today, updated automatically. For informational and entertainment purposes only — not a guarantee.",
    canonical: "https://jowaiteer.com/common-number/",
  });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/common-number/today");
        if (!alive) return;
        setData(r.data);
        writeCache(CN_CACHE_KEY, r.data);
      } catch (_e) {
        // ignore — page will show retry
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60_000); // re-check periodically so 20:00 IST rollover reflects live
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-8">
      <div className="text-center rise-in">
        <div className="ornament justify-center mb-3">
          <span className="line" /><span className="arrow" />
          <span className="uppercase text-xs font-bold tracking-widest text-[color:var(--brand-orange)]">Reference</span>
          <span className="arrow" /><span className="line" />
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[color:var(--brand-purple-deep)]">
          Juwai Teer Common Number
        </h1>
        <p className="mt-2 text-sm text-[color:var(--brand-purple)]/85">
          Auto-generated reference chart · not a guaranteed outcome
        </p>
      </div>

      <div className="card-soft mt-5 p-5 sm:p-7 rise-in rise-in-delay-1" data-testid="common-number-card">
        <div className="flex items-center justify-center gap-2 mb-4 text-[color:var(--brand-purple-deep)]">
          <Calendar size={18} className="text-[color:var(--brand-orange)]" />
          <span className="text-sm sm:text-base font-bold tracking-wide">
            DATE: <span data-testid="common-date">{loading || !data ? "—" : data.display_date}</span>
            {data && <span className="ml-2 text-[color:var(--brand-purple)]/70 font-semibold">· {data.day_name}</span>}
          </span>
        </div>

        {loading && (
          <div className="space-y-4">
            <div className="h-28 rounded-2xl bg-[color:var(--brand-purple-soft)] animate-pulse" />
            <div className="h-28 rounded-2xl bg-[color:var(--brand-purple-soft)] animate-pulse" />
          </div>
        )}

        {!loading && data && (
          <div className="grid gap-4 sm:gap-5">
            <RoundTable
              label="FIRST ROUND"
              direct={data.fr_direct}
              house={data.fr_house}
              ending={data.fr_ending}
            />
            <RoundTable
              label="SECOND ROUND"
              direct={data.sr_direct}
              house={data.sr_house}
              ending={data.sr_ending}
            />
          </div>
        )}

        <div className="mt-5 rounded-xl bg-[color:var(--brand-purple-soft)]/70 p-4 flex gap-3 items-start" data-testid="common-disclaimer">
          <Info className="text-[color:var(--brand-orange)] shrink-0 mt-0.5" size={18} />
          <p className="text-xs sm:text-sm text-[color:var(--brand-purple)]/95 leading-relaxed">
            These common numbers are for <strong>informational and entertainment purposes only</strong>.
            They are <strong>not predictions</strong>, not guaranteed, not fixed, not sure-shot and not winning numbers.
            The chart refreshes automatically every day at 8:00 PM IST for the next calendar date.
          </p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="card-soft p-4">
          <div className="font-bold text-[color:var(--brand-purple-deep)]">How is this generated?</div>
          <p className="text-[color:var(--brand-purple)]/90 mt-1">
            Automatically by the website using a fixed date-based reference. No admin input.
          </p>
        </div>
        <div className="card-soft p-4">
          <div className="font-bold text-[color:var(--brand-purple-deep)]">Timing</div>
          <p className="text-[color:var(--brand-purple)]/90 mt-1">
            Displays the active date&apos;s reference chart. At 8:00 PM IST it rolls forward to the next date.
          </p>
        </div>
      </div>
    </div>
  );
}
